import { DownloadIcon, FileIcon, LoaderIcon, TrashIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { Drawer } from 'vaul';
import { FileSize } from '../../../components/typography/file-size.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useBreakpoint } from '../../../lib/hooks/use-breakpoints.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { UploadedFile } from '../../../server/dto/crm-attachment-dto.ts';
import { api } from '../../../utils/api.ts';

type FileCardProps = {
    file: UploadedFile;
    onRemove: (file: UploadedFile) => void;
    onDownload?: (file: UploadedFile) => void;
};

export const FileCard = ({ file, onRemove, onDownload }: FileCardProps) => {
    const { t } = useLocale('tasks');
    const { t: tCommon } = useLocale('common');
    const { toast } = useToast();
    const { isSm } = useBreakpoint('sm');
    const [isDownloading, setIsDownloading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { data: conversionResult, isFetching: isConverting } =
        api.platoUser.query.uploadcare.getDocumentPreview.useQuery(
            { uuid: file.externalUuid },
            { enabled: false }, // TODO: enable this query later with APP-1634
        );

    const handleDownload = (): void => {
        setIsDownloading(true);
        try {
            // Uploadcare's `-/inline/no/<filename>/` modifier forces Content-Disposition: attachment
            const downloadUrl = `${file.cdnUrl}-/inline/no/${encodeURIComponent(file.name)}`;
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = file.name;
            a.rel = 'noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            onDownload?.(file);
        } catch (err) {
            console.error('Error downloading file:', err);
            toast({ icon: 'warning', title: t('tasks__form__download_error') });
        } finally {
            setIsDownloading(false);
        }
    };

    const previewUrl = file.isImage
        ? `${file.cdnUrl}-/scale_crop/300x300/-/format/auto/-/quality/lighter/`
        : conversionResult?.cdnUrl
          ? `${conversionResult.cdnUrl}-/scale_crop/300x300/-/format/auto/-/quality/lighter/`
          : null;

    const handleCardClick = () => {
        if (!isSm) {
            setDrawerOpen(true);
        }
    };

    return (
        <>
            <div
                className="group/file-card relative flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
                onClick={handleCardClick}
                role="presentation"
            >
                {/* Desktop: hover buttons */}
                <div className="absolute top-2 right-2 z-10 hidden gap-2 sm:flex opacity-0 transition-opacity group-hover/file-card:opacity-100">
                    <Button
                        variant="ghost"
                        size="icon"
                        rounded
                        icon={DownloadIcon}
                        onClick={handleDownload}
                        isLoading={isDownloading}
                        disabled={isDownloading}
                        aria-label={t('tasks__form__download_file')}
                        className="bg-white/80 shadow-sm"
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        rounded
                        icon={XIcon}
                        onClick={() => {
                            onRemove(file);
                        }}
                        className="bg-white/80 shadow-sm"
                    />
                </div>
                <div className="flex h-24 sm:h-32 items-center justify-center overflow-hidden bg-white p-3">
                    {previewUrl ? (
                        <img src={previewUrl} alt={file.name} className="h-full w-full object-contain" />
                    ) : isConverting ? (
                        <LoaderIcon className="h-8 w-8 animate-spin text-neutral-300" />
                    ) : (
                        <FileIcon className="h-10 w-10 text-neutral-400" />
                    )}
                </div>
                <div className="flex flex-col gap-0.5 px-3 py-2">
                    <Text size="sm" className="truncate">
                        {file.name}
                    </Text>
                    <Text size="xs" color="neutral-500">
                        <FileSize>{file.size}</FileSize>
                    </Text>
                </div>
            </div>

            <Drawer.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
                <Drawer.Portal>
                    <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
                    <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white gap-2">
                        <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-neutral-300" />
                        <div className="flex px-5 pt-4 pb-2 gap-1 flex-col">
                            <Text size="base" weight="medium" className="truncate">
                                {file.name}
                            </Text>
                            <Text size="sm" color="neutral-500">
                                <FileSize>{file.size}</FileSize>
                            </Text>
                        </div>
                        <hr className="border-neutral-100" />
                        <div className="flex flex-col gap-1 px-3 pb-3">
                            <Button
                                variant="ghost"
                                leftIcon={DownloadIcon}
                                className="justify-start"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    handleDownload();
                                }}
                            >
                                {t('tasks__form__download_file')}
                            </Button>
                            <Button
                                variant="ghost"
                                leftIcon={TrashIcon}
                                className="justify-start text-red-600"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    onRemove(file);
                                }}
                            >
                                {tCommon('form__delete')}
                            </Button>
                        </div>
                        <div className="px-3 pb-5">
                            <Button
                                variant="subtle"
                                className="w-full"
                                onClick={() => {
                                    setDrawerOpen(false);
                                }}
                            >
                                {tCommon('form__cancel')}
                            </Button>
                        </div>
                    </Drawer.Content>
                </Drawer.Portal>
            </Drawer.Root>
        </>
    );
};
